
import Vue from 'vue'
import axios from 'axios'
import { Prop, Component, Watch } from 'vue-property-decorator'
import * as Sentry from '@sentry/vue'
import ListWarnComponent, { ListWarn } from './warn.vue'
import { ListFilter } from './favourites'
import { prettyDateTime } from '../../functions/util'
import {generateParams} from "@/domains/global/functions/query"
import { getHeaderKey } from './list.utils'
import { authAxios } from '@/domains/auth/utils'

export type ItemDisplayFunctionObject<T> = { type: 'item', render: (item: T) => string }

export type ColumnDisplayFunctionObject<T> = { type: 'column', render: (item: any) => string }

export type DisplayFunctionObject<T> = ItemDisplayFunctionObject<T> | ColumnDisplayFunctionObject<T>

export type DisplayFunction<T> = DisplayFunctionObject<T> | ((item: any) => string)

export type ListColumn<T> = {
  text: string;
  type: string;
  value: keyof T;
  key?: string;
  display?: DisplayFunction<T>;
  mobileDisplay?: DisplayFunction<T>;
  filterable?: boolean;
  sortable?: boolean;
  fsExclude?: boolean;
}

export type ListHeader<T> = {
  text: string;
  type?: string;
  value?: keyof T | 'warn';
  display?: DisplayFunction<T>;
  mobileDisplay?: DisplayFunction<T>;
  filterable?: boolean;
  sortable?: boolean;
  fsExclude?: boolean;
}

type FunctionDictionary<T> = {
  [key in keyof T]?: DisplayFunctionObject<T>
}

export type ListOptions = {
  itemsPerPage?: number;
  page?: number;
  sortBy: string[];
  sortDesc: boolean[];
}

export type ListAction<T> = {
  text: string;
  method: (item: T) => void;
  visible?: (item: T) => boolean
}

export type BulkListAction<T> = {
  text: string;
  method: (item: T[]) => void
}

interface ListResult<T> {
  data: T[];
  total: number;
}

function sameValues<T>(a: T, b: T) {
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)] as (keyof T)[]))
  return keys.every(key => a[key] === b[key])
}

@Component({
  components: { ListWarnComponent }
})
export default class ListTable<T> extends Vue {
  @Prop({ type: Array, default: () => [] })
  private readonly selected!: T[]

  @Prop({ type: Array, default: () => [] })
  private readonly headers!: ListColumn<T>[]

  @Prop({ type: Array, default: () => [] })
  private readonly columns!: (keyof T)[]

  @Prop({ type: String, required: true })
  private readonly route!: string

  @Prop({ type: Object, default: () => ({ page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] }) })
  private readonly options!: ListOptions

  @Prop({ type: Array, default: () => [] })
  private readonly actions!: ListAction<T>[]

  @Prop({ type: Array, default: () => [] })
  private readonly multiActions!: BulkListAction<T>[]

  @Prop({ type: Array, default: () => [] })
  private readonly filters!: ListFilter[]

  @Prop({ type: [Array, Object], default: null })
  private readonly warn?: ListWarn<T> | ListWarn<T>[]

  @Prop({ type: Boolean, default: false })
  private readonly manualOptions!: boolean

  @Prop({ type: Array, default: () => [] })
  private readonly queryParams!: string[][]

  @Prop({ type: Object, default: null })
  private readonly authDataSearch!: any

  private items: T[] = []

  private total = 0

  private loading = false

  private error = false

  private rowsPerPageItems: number[] = [5, 10, 25, 50, 75, 100]

  private internalOptions: ListOptions = this.options

  get hasWarn () {
    return !!this.warn && (!Array.isArray(this.warn) || !!this.warn.length)
  }

  get hasMultiWarn () {
    return !!this.warn && Array.isArray(this.warn) && !!this.warn.length
  }

  get warnItems () {
    return Array.isArray(this.warn) ? this.warn : []
  }

  private get _selected() {
    return this.selected
  }

  private set _selected(value: T[]) {
    this.$emit('update:selected', value)
  }

  private get displayFunctions() {
    const functions: FunctionDictionary<T> = {}
    this.headers.forEach((column) => {
      if (column.display) {
        if (typeof column.display === 'function') {
          functions[column.value] = {
            type: 'column',
            render: column.display
          }
        } else {
          functions[column.value] = column.display
        }
      }
    })
    return functions
  }

  private get mobileDisplayFunctions() {
    const functions: FunctionDictionary<T> = {}
    this.headers.forEach((column) => {
      if (column.mobileDisplay) {
        if (typeof column.mobileDisplay === 'function') {
          functions[column.value] = {
            type: 'column',
            render: column.mobileDisplay
          }
        } else {
          functions[column.value] = column.mobileDisplay
        }
      }
    })
    return functions
  }

  private get mobileView() {
    return ['xs', 'sm'].includes(this.$vuetify.breakpoint.name)
  }

  private get _headers() {
    const validHeaders: ListHeader<T>[] = []
    if (this.hasWarn) {
      validHeaders.push({ text: '', value: 'warn', filterable: false, sortable: false })
    }
    validHeaders.push(...this.headers)
    if (this.actions.length) {
      validHeaders.push({ text: 'Actions', filterable: false,  sortable: false })
    }
    return validHeaders
  }

  @Watch('filters', { deep: true })
  private filtersChanged() {
    this.updateResults()
  }

  @Watch('options', { deep: true })
  private optionsChanged(value: ListOptions) {
    this.internalOptions = { ...value }
  }

  @Watch('internalOptions', { deep: true })
  private _optionsChanged(value: ListOptions) {
    if (!this.manualOptions) {
      this.updateResults(value)
    } else {
      if (!sameValues(this.options, value)) {
        this.$emit('update:options', value)
      }
    }
  }

  mounted() {
    this.updateResults()
  }

  private validActions(item: T) {
    return this.actions.filter(action => !action.visible || action.visible(item))
  }

  public async updateResults(options?: ListOptions) {
    this.loading = true
    const data = this.authDataSearch ? await this.getAuthData(options) : await this.getData(options)
    this.items = data.items
    this.total = data.total
    this.loading = false
  }

  private async getData(options?: ListOptions) {
    const opt = options || this.internalOptions
    this.error = false
    const params = generateParams(opt, this.filters, this.queryParams)

    try {
      const response = await axios.get<ListResult<T>>(`${this.route}?${params}`)
      return {
        items: response.data.data,
        total: response.data.total
      }
    } catch (error) {
      Sentry.captureException(error)
      this.error = true
      return { items: [], total: 0 }
    }
  }

  private async getAuthData(options?: ListOptions) {
    const opt = options || this.internalOptions
    this.error = false
    const params = generateParams(opt, this.filters, this.queryParams)

    try {
      const response = await authAxios.post<any>(`accounts?${params}`, this.authDataSearch)
      const data: T[] = response.data
      
      return {
        items: data,
        total: data.length
      }
    } catch (error) {
      Sentry.captureException(error)
      this.error = true
      return { items: [], total: 0 }
    }
  }

  private renderValue(item: T, column: keyof T) {
    const displayFn = this.displayFunctions[column]
    const mobileDisplayFn = this.mobileDisplayFunctions[column]
    const columnData = this.headers.find(h => getHeaderKey(h) === column)

    if (!this.mobileView && !displayFn && columnData && columnData.type === 'date') {
      return prettyDateTime(item[column] as any) // eslint-disable-line
    }

    if (!this.mobileView) {
      return this.getRenderValue(displayFn, item, column)
    } else {
      return this.getRenderValue(mobileDisplayFn || displayFn, item, column)
    }
  }

  private getRenderValue(displayFn: DisplayFunctionObject<T> | undefined, item: T, column: keyof T) {
    if (!displayFn) {
      return item[column]
    }

    if (displayFn.type === 'column') {
      const fn = displayFn as ColumnDisplayFunctionObject<T>
      return fn.render(item[column])
    } else {
      const fn = displayFn as ItemDisplayFunctionObject<T>
      return fn.render(item)
    }
  }

  private fullstoryExclude(currentColumn: string) {
    return !!this.headers.find((column) => {
      return (column.value === currentColumn) && (!!column.fsExclude)
    })
  }
}
